import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { database } from '../Utils/firebaseConfig';
import {
  getEventWriteCollection,
  getEventReadCollection,
  mapFirestoreEventDoc,
  filterEventsOverlappingYear,
  mergeCalendarEvents,
} from '../Utils/eventCollections';

const LOAD_TIMEOUT_MS = 5000;

/**
 * Subscribes to Firestore calendar events. In dev overlay mode, listens to both
 * read (production Rentman) and write (development) collections and merges them.
 */
export function useMergedCalendarEvents(selectedDate) {
  const writeCollection = getEventWriteCollection();
  const readCollection = getEventReadCollection();
  const useReadOverlay = Boolean(readCollection);

  const [events, setEvents] = useState(undefined);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  const readEventsRef = useRef([]);
  const writeEventsRef = useRef([]);
  const loadedRef = useRef({ read: !useReadOverlay, write: false });

  const publishMerged = useCallback(() => {
    const merged = mergeCalendarEvents(
      readEventsRef.current,
      writeEventsRef.current,
      useReadOverlay,
    );
    setEvents(merged);

    const readReady = loadedRef.current.read;
    const writeReady = loadedRef.current.write;
    if (readReady && writeReady) {
      setEventsLoaded(true);
    }
  }, [useReadOverlay]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setEventsLoaded((loaded) => {
        if (!loaded) {
          console.log('[useMergedCalendarEvents] Loading timeout — marking events as loaded');
        }
        return true;
      });
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [selectedDate, writeCollection, readCollection]);

  useEffect(() => {
    if (!selectedDate || !(selectedDate instanceof Date)) {
      return undefined;
    }

    const queryId = Math.random().toString(36).slice(2, 9);
    const startOfYear = new Date(Date.UTC(selectedDate.getFullYear(), 0, 1, 0, 0, 0, 0));
    const endOfYear = new Date(Date.UTC(selectedDate.getFullYear(), 11, 31, 23, 59, 59, 999));

    readEventsRef.current = [];
    writeEventsRef.current = [];
    loadedRef.current = { read: !useReadOverlay, write: false };
    setEventsLoaded(false);

    console.log(`[useMergedCalendarEvents:${queryId}] Listening`, {
      writeCollection,
      readCollection: readCollection || '(single collection)',
      useReadOverlay,
      year: selectedDate.getFullYear(),
    });

    const attachListener = (collectionName, kind) => {
      const ref = collection(database, collectionName);
      const q = query(ref, where('start', '<=', endOfYear));

      return onSnapshot(
        q,
        (snapshot) => {
          try {
            const mapped = snapshot.docs.map(mapFirestoreEventDoc);
            const overlapping = filterEventsOverlappingYear(mapped, startOfYear, endOfYear);
            if (kind === 'read') {
              readEventsRef.current = overlapping;
              loadedRef.current.read = true;
            } else {
              writeEventsRef.current = overlapping;
              loadedRef.current.write = true;
            }
            console.log(
              `[useMergedCalendarEvents:${queryId}] ${kind} snapshot:`,
              overlapping.length,
              'events in',
              collectionName,
            );
            publishMerged();
          } catch (error) {
            console.error(`[useMergedCalendarEvents:${queryId}] ${kind} processing error:`, error);
            loadedRef.current[kind] = true;
            publishMerged();
            setEventsLoaded(true);
          }
        },
        (error) => {
          console.error(`[useMergedCalendarEvents:${queryId}] ${kind} listener error:`, error);
          loadedRef.current[kind] = true;
          setEventsLoaded(true);
        },
      );
    };

    const unsubWrite = attachListener(writeCollection, 'write');
    const unsubRead = readCollection ? attachListener(readCollection, 'read') : null;

    return () => {
      console.log(`[useMergedCalendarEvents:${queryId}] Cleaning up listeners`);
      unsubWrite();
      if (unsubRead) unsubRead();
    };
  }, [selectedDate, writeCollection, readCollection, useReadOverlay, publishMerged]);

  return {
    events,
    setEvents,
    eventsLoaded,
    setEventsLoaded,
    writeCollection,
    readCollection,
    useReadOverlay,
  };
}
